import { useQuery } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { Store } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "@/components/animate-ui/icons/check";
import { merchantQueryOptions } from "@/api/vendure/merchant/merchant.query.options";
import { orderDetailQueryOptions } from "@/api/vendure/order/order.query.options";
import { OrderState } from "@/api/vendure/order/order.types";
import {
  formatTimeLeft,
  getGaugeInfo,
} from "@/components/business/order-confirm/order-confirm.utils";
import { lastOrderCodeAtom, lastOrderSlugAtom } from "@/stores/cart.store";

const SIZE = 64;
const STROKE = 5;
const RADIUS = SIZE / 2 - STROKE;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const CHECK_STATES = new Set([OrderState.Ready, OrderState.Delivering, OrderState.Delivered]);

function TimeLeft({ minsLeft, isPending }: { minsLeft: number; isPending: boolean }) {
  const { t } = useTranslation();
  const raw = isPending ? { value: "~25", unit: "min" as const } : formatTimeLeft(minsLeft);
  const unit = raw.unit === "hr"
    ? t("business.orderConfirm.unitHr")
    : t("business.orderConfirm.unitMin");
  return (
    <>
      <span data-testid="order-bubble.time-value" className="text-white text-base font-bold leading-none tabular-nums">{raw.value}</span>
      <span data-testid="order-bubble.time-unit" className="text-white/80 text-[8px] font-semibold uppercase tracking-wide">{unit}</span>
    </>
  );
}

export function OrderTrackingBubble() {
  const lastOrderCode = useAtomValue(lastOrderCodeAtom);
  const lastOrderSlug = useAtomValue(lastOrderSlugAtom);
  const navigate = useNavigate();
  const routerState = useRouterState();
  const constraintsRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const isOnConfirmPage = routerState.location.pathname.includes("order-confirm");

  const { data: detail } = useQuery(
    orderDetailQueryOptions(lastOrderCode ?? "", !!lastOrderCode),
  );

  const { data: venues } = useQuery({
    ...merchantQueryOptions(lastOrderSlug ?? ""),
    enabled: !!lastOrderSlug,
  });

  const show = !!lastOrderCode && !!lastOrderSlug && !isOnConfirmPage;
  const venueImageUrl = venues?.[0]?.image_url;
  const state = (detail?.state ?? "") as OrderState;
  const showCheck = CHECK_STATES.has(state);
  const isPending = state === OrderState.Pending || !detail;

  const { minsLeft, progress } = getGaugeInfo({
    pickupTime: detail?.pickupTime ?? detail?.context.pickupTime,
    scheduledFor: detail?.scheduledFor,
    orderPlacedAt: detail?.orderPlacedAt,
    serviceMode: detail?.context.serviceMode,
  });

  const dashOffset = CIRCUMFERENCE * progress;

  function handleClick() {
    if (dragging.current) return;
    if (!lastOrderSlug || !lastOrderCode) return;
    navigate({
      to: "/business/$slug/order-confirm",
      params: { slug: lastOrderSlug },
      search: { code: lastOrderCode },
    });
  }

  return (
    <AnimatePresence>
      {show && (
        <div
          ref={constraintsRef}
          className="fixed inset-0 pointer-events-none z-30"
        >
          <motion.button
            type="button"
            drag
            dragConstraints={constraintsRef}
            dragElastic={0.12}
            dragMomentum={false}
            onDragStart={() => { dragging.current = true; }}
            onDragEnd={() => { requestAnimationFrame(() => { dragging.current = false; }); }}
            onClick={handleClick}
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.4, opacity: 0 }}
            transition={{ type: "spring", stiffness: 340, damping: 26, delay: 0.15 }}
            className="absolute bottom-1/2 translate-y-1/2 right-4 pointer-events-auto focus:outline-none cursor-grab active:cursor-grabbing"
            style={{ width: SIZE, height: SIZE }}
            aria-label="View order tracking"
          >
            <svg
              width={SIZE}
              height={SIZE}
              style={{ position: "absolute", top: 0, left: 0, transform: "rotate(-90deg)" }}
              aria-hidden="true"
            >
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                className="stroke-muted"
                strokeWidth={STROKE}
              />
              <motion.circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                className="stroke-primary"
                strokeWidth={STROKE}
                strokeLinecap="round"
                strokeDasharray={CIRCUMFERENCE}
                initial={{ strokeDashoffset: 0 }}
                animate={{ strokeDashoffset: dashOffset }}
                transition={{ duration: 1, ease: "easeOut" }}
              />
            </svg>

            <div className="absolute rounded-full overflow-hidden" style={{ inset: STROKE }}>
              {venueImageUrl ? (
                <img src={venueImageUrl} alt="restaurant" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 bg-muted flex items-center justify-center">
                  <Store className="w-6 h-6 text-muted-foreground" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center">
                {showCheck
                  ? <Check size={22} animate="default" className="text-white" />
                  : <TimeLeft minsLeft={minsLeft} isPending={isPending} />}
              </div>
            </div>
          </motion.button>
        </div>
      )}
    </AnimatePresence>
  );
}
